import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash, BookmarkSimple, X, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

// Checklist for one shift: staff tick tasks, admins edit the list.
export const ShiftTasksDialog = ({ month, shift, onClose, onChanged, onEdit }) => {
  const { API, user, language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  const isAdmin = user?.role === 'admin';
  const [tasks, setTasks] = useState(shift?.tasks || []);
  const [draft, setDraft] = useState('');
  const [templates, setTemplates] = useState([]);
  const loadTemplates = useCallback(() => {
    if (isAdmin) API.get('/work-task-templates').then(({ data }) => setTemplates(data)).catch(() => {});
  }, [API, isAdmin]);
  useEffect(() => { setTasks(shift?.tasks || []); }, [shift]);
  useEffect(() => { if (shift) loadTemplates(); }, [shift, loadTemplates]);
  if (!shift) return null;
  const ref = { date: shift.date, person: shift.person, start: shift.start };

  const apply = (data) => { setTasks(data.tasks); onChanged(data); };
  const toggle = async (t, done) => {
    try { const { data } = await API.post(`/work-plans/${month}/tasks/toggle`, { ...ref, task_id: t.id, done }); apply(data); }
    catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };
  const save = async (texts) => {
    try { const { data } = await API.put(`/work-plans/${month}/tasks`, { ...ref, tasks: texts }); apply(data); }
    catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };
  const add = () => { if (draft.trim()) { save([...tasks.map(t => t.text), draft.trim()]); setDraft(''); } };
  const saveTemplate = async () => {
    if (!draft.trim()) { toast.error(L('Type a task first', 'Zuerst eine Aufgabe eintippen')); return; }
    try { await API.post('/work-task-templates', { text: draft.trim() }); toast.success(L('Template saved', 'Vorlage gespeichert')); setDraft(''); loadTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };
  const removeTemplate = async (tp) => {
    if (!window.confirm(L(`Delete template "${tp.text}"?`, `Vorlage "${tp.text}" löschen?`))) return;
    try { await API.delete(`/work-task-templates/${tp.id}`); loadTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  return (
    <Dialog open={!!shift} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="shift-tasks-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{shift.person} · {shift.start}–{shift.end}</DialogTitle>
          <DialogDescription>
            {new Date(`${shift.date}T00:00:00`).toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {tasks.length === 0 && <p className="text-sm text-slate-400" data-testid="shift-tasks-empty">{L('No tasks for this shift.', 'Keine Aufgaben für diese Schicht.')}</p>}
          {tasks.map(t => (
            <div key={t.id} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2" data-testid={`shift-task-${t.id}`}>
              <Checkbox checked={!!t.done} onCheckedChange={(v) => toggle(t, !!v)} data-testid={`shift-task-check-${t.id}`} />
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${t.done ? 'line-through text-slate-400' : 'text-slate-800'}`}>{t.text}</div>
                {t.done && t.done_by_name && (
                  <div className="text-[11px] text-emerald-700">
                    {L('Done by', 'Erledigt von')} {t.done_by_name} · {new Date(t.done_at).toLocaleString('de-CH', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                  </div>
                )}
              </div>
              {isAdmin && (
                <button type="button" onClick={() => save(tasks.filter(x => x.id !== t.id).map(x => x.text))}
                  className="text-slate-400 hover:text-red-600" data-testid={`shift-task-remove-${t.id}`}>
                  <Trash size={16} />
                </button>
              )}
            </div>
          ))}
          {isAdmin && (
            <div className="flex gap-2 pt-2">
              <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
                placeholder={L('New task, e.g. clean courts', 'Neue Aufgabe, z.B. Plätze reinigen')} data-testid="shift-task-input" />
              <Button onClick={add} className="bg-slate-900 text-white hover:bg-slate-700" data-testid="shift-task-add-btn"><Plus size={16} /></Button>
              <Button variant="outline" onClick={saveTemplate} title={L('Save as template', 'Als Vorlage speichern')} data-testid="shift-task-save-template-btn">
                <BookmarkSimple size={16} />
              </Button>
            </div>
          )}
          {isAdmin && templates.length > 0 && (
            <div className="pt-2" data-testid="task-templates">
              <div className="text-xs font-semibold text-slate-500 mb-1.5">{L('Templates — tap to add', 'Vorlagen — antippen zum Übernehmen')}</div>
              <div className="flex flex-wrap gap-1.5">
                {templates.map(tp => {
                  const used = tasks.some(t => t.text === tp.text);
                  return (
                    <span key={tp.id} className={`inline-flex items-center rounded-full border text-xs font-medium ${used ? 'bg-slate-100 text-slate-400 border-slate-200' : 'bg-white text-slate-700 border-slate-300'}`}>
                      <button type="button" disabled={used} onClick={() => save([...tasks.map(t => t.text), tp.text])}
                        className="pl-3 pr-1.5 py-1 hover:text-slate-900 disabled:cursor-default" data-testid={`task-template-${tp.id}`}>
                        + {tp.text}
                      </button>
                      <button type="button" onClick={() => removeTemplate(tp)} className="pr-2 text-slate-300 hover:text-red-600"
                        title={L('Delete template', 'Vorlage löschen')} data-testid={`task-template-delete-${tp.id}`}>
                        <X size={10} />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {isAdmin && onEdit && (
            <Button variant="outline" onClick={() => onEdit(shift)} className="w-full mt-2" data-testid="shift-edit-open-btn">
              <PencilSimple size={16} className="mr-2" /> {L('Change shift times / person', 'Schicht ändern (Zeit/Person)')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
